/**
 * Routes pour le module School
 * Chemins canoniques selon la hiérarchie:
 *
 * Hiérarchie: Établissement → Classe → Enseignement → Chapitre → Leçon → Ressource
 *
 * Établissements:
 * - /establishments → liste (admin)
 * - /establishments/:id → détail
 * - /establishments/:id/edit → édition
 * - /establishments/:id/classes/new → créer une classe
 *
 * Classes:
 * - /classes → liste
 * - /classes/:id → détail
 * - /classes/:id/edit → édition
 * - /classes/:id/members → membres de la classe
 * - /classes/:id/schedule → emploi du temps
 *
 * Enseignements (Teachings):
 * - /teachings → liste
 * - /teachings/:id → détail
 * - /teachings/:id/edit → édition
 * - /teachings/new → créer un enseignement
 *
 * Chapitres:
 * - /chapters/:id → détail
 * - /chapters/:id/edit → édition
 * - /chapters/new → créer un chapitre
 *
 * Leçons:
 * - /lessons → liste
 * - /lessons/:id → détail
 * - /lessons/:id/edit → édition
 * - /lessons/:id/resources → ressources de la leçon
 * - /lessons/new → créer une leçon
 * - /lessons/calendar → calendrier des leçons
 *
 * Ressources:
 * - /resources → liste
 * - /resources/:id → détail
 * - /resources/:id/edit → édition
 * - /resources/new → créer une ressource
 *
 * Vues "Mon...":
 * - /me/establishment → établissement de l'utilisateur
 * - /me/class → classe de l'utilisateur
 */

import { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { RequireSection } from './guards/RequireSection';
import { RequireEstablishment } from './guards/RequireEstablishment';

// Import lazy des pages
import { lazy } from 'react';

// Pages Establishments
// const EstablishmentsListPage = lazy(
//   () => import("@/pages/establishments/EstablishmentsListPage")
// );
// const EstablishmentDetailPage = lazy(
//   () => import("@/pages/establishments/EstablishmentDetailPage")
// );
// const EstablishmentCreatePage = lazy(
//   () => import("@/pages/establishments/EstablishmentCreatePage")
// );
// const EstablishmentEditPage = lazy(
//   () => import("@/pages/establishments/EstablishmentEditPage")
// );

// Pages Classes
const ClassesListPage = lazy(() => import('@/pages/Classes/ClassesListPage'));
const ClassesCreatePage = lazy(
  () => import('@/pages/Classes/ClassesCreatePage')
);
const ClassesEditPage = lazy(() => import('@/pages/Classes/ClassesEditPage'));
const ClassesDetailPage = lazy(
  () => import('@/pages/Classes/ClassesDetailPage')
);
const StudentProgressPage = lazy(
  () => import('@/pages/Classes/StudentProgressPage')
);

// Pages Teachings
const TeachingsListPage = lazy(
  () => import('@/pages/Teachings/TeachingsListPage')
);
const TeachingDetailPage = lazy(
  () => import('@/pages/Teachings/TeachingDetailPage')
);
const TeachingCreatePage = lazy(
  () => import('@/pages/Teachings/TeachingCreatePage')
);
const TeachingEditPage = lazy(
  () => import('@/pages/Teachings/TeachingEditPage')
);

// Pages Chapters
const ChaptersListPage = lazy(
  () => import('@/pages/Chapters/ChaptersListPage')
);
const ChapterDetailPage = lazy(
  () => import('@/pages/Chapters/ChapterDetailPage')
);
const ChapterCreatePage = lazy(
  () => import('@/pages/Chapters/ChapterCreatePage')
);
const ChapterEditPage = lazy(() => import('@/pages/Chapters/ChapterEditPage'));

// Pages Lessons
const LessonsListPage = lazy(() => import('@/pages/Lessons/LessonsListPage'));
const LessonDetailPage = lazy(() => import('@/pages/Lessons/LessonDetailPage'));
const LessonCreatePage = lazy(() => import('@/pages/Lessons/LessonCreatePage'));
const LessonEditPage = lazy(() => import('@/pages/Lessons/LessonEditPage'));



// Pages Resources
const ResourcesListPage = lazy(
  () => import('@/pages/Resources/ResourcesListPage')
);
const ResourceDetailPage = lazy(
  () => import('@/pages/Resources/ResourceDetailPage')
);
const ResourceCreatePage = lazy(
  () => import('@/pages/Resources/ResourceCreatePage')
);
const ResourceEditPage = lazy(
  () => import('@/pages/Resources/ResourceEditPage')
);

// Pages personnelles (Me)
const MyEstablishmentPage = lazy(
  () => import('@/pages/Me/MyEstablishmentPage')
);
const MyEstablishmentPageNew = lazy(
  () => import('@/pages/Establishment/MyEstablishmentPage')
);
const EstablishmentDetailPage = lazy(
  () => import('@/pages/Establishment/EstablishmentDetailPage')
);
const EstablishmentEditPage = lazy(
  () => import('@/pages/Establishment/EstablishmentEditPage')
);
// const MyClassPage = lazy(() => import('@/pages/me/MyClassPage'));

// Pages Members (Accès & Membres)
const MembersListPage = lazy(() => import('@/pages/Members/MembersListPage'));
const MemberDetailPage = lazy(() => import('@/pages/Members/MemberDetailPage'));
const MemberEditPage = lazy(() => import('@/pages/Members/MemberEditPage'));

/**
 * Routes du module School
 * Toutes les routes sont protégées par authentification
 * Certaines ont une vérification de section supplémentaire
 */
export const schoolRoutes: RouteObject[] = [
  // ============================================
  // Classes
  // ============================================
  {
    path: 'classes',
    element: <RequireEstablishment />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <ClassesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'new',
        element: (
          <ProtectedRoute>
            <ClassesCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'students/:userId',
        element: (
          <ProtectedRoute>
            <StudentProgressPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id',
        element: (
          <ProtectedRoute>
            <ClassesDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id/edit',
        element: (
          <ProtectedRoute>
            <ClassesEditPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // ============================================
  // Enseignements (Teachings)
  // ============================================
  {
    path: 'teachings',
    element: <RequireEstablishment />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <TeachingsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'new',
        element: (
          <ProtectedRoute>
            <TeachingCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id',
        element: (
          <ProtectedRoute>
            <TeachingDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id/edit',
        element: (
          <ProtectedRoute>
            <TeachingEditPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // ============================================
  // Chapitres
  // ============================================
  {
    path: 'chapters',
    element: <RequireEstablishment />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <ChaptersListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'new',
        element: (
          <ProtectedRoute>
            <ChapterCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id',
        element: (
          <ProtectedRoute>
            <ChapterDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id/edit',
        element: (
          <ProtectedRoute>
            <ChapterEditPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // ============================================
  // Leçons
  // ============================================
  {
    path: 'lessons',
    element: <RequireEstablishment />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <LessonsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'new',
        element: (
          <ProtectedRoute>
            <LessonCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id',
        element: (
          <ProtectedRoute>
            <LessonDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id/edit',
        element: (
          <ProtectedRoute>
            <LessonEditPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // ============================================
  // Ressources
  // ============================================
  {
    path: 'resources',
    element: <RequireEstablishment />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <ResourcesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'new',
        element: (
          <ProtectedRoute>
            <ResourceCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id',
        element: (
          <ProtectedRoute>
            <ResourceDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id/edit',
        element: (
          <ProtectedRoute>
            <ResourceEditPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // ============================================
  // Sessions
  // ============================================
  {
    path: 'sessions',
    children: [
      // TODO: Ajouter les routes de session
      // - index
    ],
  },

  // ============================================
  // Vues "Mon..." (personnalisées selon l'utilisateur)
  // Pas de RequireSection car ces vues sont personnelles
  // et accessibles à tous les utilisateurs authentifiés
  // ============================================
  {
    path: 'me',
    children: [
      {
        path: 'establishment',
        element: (
          <ProtectedRoute>
            <MyEstablishmentPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // ============================================
  // Membres de l'établissement (Accès & Membres)
  // ============================================
  {
    path: 'members',
    element: <RequireEstablishment />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute
            roles={['admin', 'principal', 'administrative_manager', 'support']}
          >
            <MembersListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id',
        element: (
          <ProtectedRoute>
            <MemberDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id/edit',
        element: (
          <ProtectedRoute>
            <MemberEditPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // ============================================
  // Page "Mon établissement" (nouvelle route)
  // Redirige vers /establishment/:id
  // ============================================
  {
    path: 'my-establishment',
    element: (
      <ProtectedRoute>
        <MyEstablishmentPageNew />
      </ProtectedRoute>
    ),
  },

  // ============================================
  // Pages établissement (détail et édition)
  // ============================================
  {
    path: 'establishment',
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <MyEstablishmentPageNew />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id',
        element: (
          <ProtectedRoute>
            <EstablishmentDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':id/edit',
        element: (
          <ProtectedRoute>
            <EstablishmentEditPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
];
